/**
 * Creates FormData for uploading a single file to a dedicated endpoint.
 * Use this for avatar uploads, logo uploads, etc.
 *
 * @param file - The file to upload
 * @param fieldName - The field name (default: 'file')
 * @returns FormData ready for submission
 *
 * @example
 * ```typescript
 * const formData = createSingleFileFormData(avatarFile)
 * await post(`/users/${userId}/avatar`, formData)
 * ```
 */
export function createSingleFileFormData(
  file: File,
  fieldName: string = 'file'
): FormData {
  const formData = new FormData()
  formData.append(fieldName, file)
  return formData
}

export interface ObjectToFormDataOptions {
  /** Field name for file arrays (will be excluded from normal processing) */
  filesField?: string
  /**
   * Fields that should be JSON.stringified.
   * Arrays and objects in this list will be stringified.
   * Arrays NOT in this list will be converted to comma-separated strings.
   */
  jsonFields?: string[]
}

/**
 * Converts an object to FormData with smart handling of files, objects, and arrays.
 * Use this for entity creation that includes documents and complex nested data.
 *
 * @param data - The data object to convert
 * @param options - Configuration for file handling and JSON serialization
 * @returns FormData ready for submission
 *
 * @example
 * ```typescript
 * const formData = objectToFormData(
 *   { ...siteData, documents: files },
 *   {
 *     filesField: 'documents',
 *     jsonFields: ['responsibleContact', 'operatingHours']
 *   }
 * )
 * await post('/sites', formData)
 * ```
 */
export function objectToFormData<T extends object>(
  data: T,
  options?: ObjectToFormDataOptions
): FormData {
  const formData = new FormData()

  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined) {
      continue
    }

    // Skip files field if specified (handle separately)
    if (options?.filesField && key === options.filesField) {
      continue
    }

    // Handle arrays that should be stringified (only if in jsonFields)
    if (Array.isArray(value) && options?.jsonFields?.includes(key)) {
      formData.append(key, JSON.stringify(value))
    }
    // Handle objects (but not File instances)
    else if (typeof value === 'object' && !(value instanceof File)) {
      formData.append(key, JSON.stringify(value))
    }
    // Handle primitives and arrays NOT in jsonFields
    else {
      formData.append(key, String(value))
    }
  }

  // Handle files if filesField is specified
  if (options?.filesField && data[options.filesField as keyof T]) {
    const files = data[options.filesField as keyof T] as unknown as File[]
    const filesFieldName = options.filesField
    if (Array.isArray(files)) {
      files.forEach((file) => {
        formData.append(filesFieldName, file)
      })
    }
  }

  return formData
}
